import { Component, OnDestroy, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FileUploadModule, FileUploadValidators } from '@iplab/ngx-file-upload';
import { ProjectService } from '../../services/project/project.service';
import { Dialog } from '../../interfaces/create-dialog.interface';
import * as zip from '@zip.js/zip.js';
import { AppStateService } from '../../services/app-state/app-state.service';
import { ToastrService } from 'ngx-toastr';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

// Define an interface to represent project version details
interface Version {
  versionId: number; // Unique ID of the version
  majorVersion: number; // Major version number
  minorVersion: number; // Minor version number
}

/**
 * Component for importing a project from a zip file.
 * This component handles the project import process, including file selection,
 * data extraction from zip files, and project creation.
 * The user is guided through multiple steps, including selecting versions,
 * defining connections, and entering project name information.
 */
@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-import-project-dialog',
  templateUrl: './import-project-dialog.component.html',
  styleUrls: ['./import-project-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressBarModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    FileUploadModule,
  ],
})
export class ImportProjectDialogComponent implements OnDestroy {
  data = inject<Dialog>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<ImportProjectDialogComponent>>(MatDialogRef);
  private formBuilder = inject(FormBuilder);
  private readonly projectService = inject(ProjectService);
  private appStateService = inject(AppStateService);
  private toastr = inject(ToastrService);

  // Tracking the current step of the import process (e.g., file upload, version selection)
  public importStep = signal(1);
  public importInProgress = signal(false); // Flag to track if import is in progress
  public importComplete = signal(false); // Flag to track if import is complete
  public projectName = signal(''); // Holds the name of the imported project
  public versions = signal<Version[]>([]); // List of available project versions
  public selectedVersionId = signal<number | null>(null); // ID of the selected version
  public connections = signal<{ name: string; secret: string }[]>([]); // List of connection objects
  public warnings = signal<string[]>([]); // List of warnings that arise during the import process

  // Forms to handle file upload, project name, and connection secrets
  fileUploadForm: FormGroup;
  projectNameForm: FormGroup;
  connectionsForm: FormGroup;
  passwordForm: FormGroup;

  constructor() {
    // Initialize the forms with their respective controls and validators
    this.fileUploadForm = this.formBuilder.group({
      files: new FormControl<File[]>(null as any, [
        FileUploadValidators.filesLimit(1), // Limit to 1 file
        FileUploadValidators.accept(['.zip']), // Only allow .zip files
      ]),
    });
    this.projectNameForm = this.formBuilder.group({
      projectName: ['', [Validators.required, this.uniqueProjectNameValidator.bind(this)]],
    });
    this.connectionsForm = this.formBuilder.group({}); // Initialize empty connections form
    this.passwordForm = this.formBuilder.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  private uniqueProjectNameValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const trimmedName = control.value.trim().toLowerCase();
    const exists = this.appStateService.projects().some(
      (p) => p.name.trim().toLowerCase() === trimmedName,
    );
    return exists ? { notUnique: true } : null;
  }

  // eslint-disable-next-line
  ngOnDestroy(): void {
    // This method is intentionally empty
  }

  /**
   * Handles the file selection process.
   * Extracts and processes the selected zip file to retrieve project data.
   */
  public async onFileSelected() {
    if (this.fileUploadForm.valid) {
      const file = this.fileUploadForm.get('files')?.value?.[0]; // Get selected file
      if (file) {
        try {
          await this.extractProjectData(file); // Extract data from the zip file
          this.moveToNextStep(); // Move to the next step in the import process
        } catch (error) {
          console.error('Error processing zip file:', error); // Log any error encountered during extraction
          // Display an error message to the user
        }
      }
    }
  }

  /**
   * Handles the version selection.
   * Moves to the next step after a version is selected.
   */
  public onVersionSelected(versionId: number | null) {
    if (versionId !== null) {
      this.selectedVersionId.set(versionId);
      this.moveToNextStep();
    }
  }

  /**
   * Handles submission of connection secrets.
   * Updates the connection objects with secrets entered by the user.
   */
  public onConnectionsSubmitted() {
    Object.keys(this.connectionsForm.controls).forEach((key) => {
      const connection = this.connections().find((c) => c.name === key);
      if (connection) {
        connection.secret = this.connectionsForm.get(key)?.value; // Store the secret for the connection
      }
    });
    this.moveToNextStep(); // Move to the next step
  }

  /**
   * Handles the submission of the project name.
   * Ensures that the project name is unique before proceeding with the import.
   */
  public onProjectNameSubmitted() {
    if (this.projectNameForm.valid && this.passwordForm.valid) {
      const password = this.passwordForm.get('password')?.value;
      this.importProject(password);
    }
  }

  /**
   * Moves to the next step in the import process based on the current step.
   * Logic handles conditional transitions between steps.
   */
  private moveToNextStep() {
    if (this.importStep() === 1) {
      this.importStep.set(this.versions().length > 1 ? 2 : 4);
    } else if (this.importStep() === 2) {
      this.importStep.set(4);
    } else {
      this.importStep.update(v => v + 1);
    }
  }

  /**
   * Extracts project data from the selected zip file.
   * Processes various project files (metadata, project.json, versions.json, connections.json)
   * to populate the component's state.
   */
  private async extractProjectData(file: File) {
    const requiredFiles = [
      'metadata.json',
      'project.json',
      'versions.json',
      'connections.json',
    ];
    const reader = new zip.ZipReader(new zip.BlobReader(file));

    try {
      const entries = await reader.getEntries();
      const fileContents: { [key: string]: string } = {};

      for (const entry of entries) {
        if (requiredFiles.includes(entry.filename)) {
          if ((entry as any).getData) {
            const writer = new zip.TextWriter();
            const content = await (entry as any).getData?.(writer);
            fileContents[entry.filename] = content as string;
          } else {
            throw new Error(`Unable to read data from ${entry.filename}`);
          }
        }
      }

      // Validate all required files are present
      for (const requiredFile of requiredFiles) {
        if (!fileContents[requiredFile]) {
          throw new Error(`${requiredFile} not found in the zip file`);
        }
      }

      // Process metadata.json
      const metadata = JSON.parse(fileContents['metadata.json']);
      const SUPPORTED_VERSIONS = [1, 4, 5];
      const metadataVersionId = metadata.version_id ?? metadata.versionId;
      if (
        typeof metadata.format !== 'string' ||
        !/^[a-zA-Z0-9]+(\.[a-zA-Z0-9]+)*$/.test(metadata.format) ||
        !SUPPORTED_VERSIONS.includes(metadataVersionId)
      ) {
        throw new Error('Invalid or unsupported project format');
      }

      // Process project.json
      const projectData = JSON.parse(fileContents['project.json']);
      this.projectName.set(projectData.name);
      this.selectedVersionId.set(projectData.currentVersionId);
      this.projectNameForm.patchValue({ projectName: this.projectName() });

      // Process versions.json
      const parsedVersions: Version[] = JSON.parse(fileContents['versions.json']);
      this.versions.set([...parsedVersions].sort((a, b) => b.versionId - a.versionId));

      // Process connections.json
      const connectionsData = JSON.parse(fileContents['connections.json']);
      this.connections.set(connectionsData);
      this.initializeConnectionsForm();

      console.log('Extracted project data:', {
        projectName: this.projectName(),
        selectedVersionId: this.selectedVersionId(),
        versions: this.versions(),
        connections: this.connections(),
      });
    } catch (error) {
      console.error('Error extracting project data:', error);
      this.toastr.error(
        'Failed to extract project data. Please check the file and try again.',
      );
      throw error;
    } finally {
      await reader.close();
    }
  }

  private initializeConnectionsForm() {
    this.connectionsForm = this.formBuilder.group({});
    this.connections().forEach((conn) => {
      this.connectionsForm.addControl(
        conn.name,
        this.formBuilder.control('', Validators.required),
      );
    });
  }

  /**
   * Handles the import process by sending the extracted data to the backend.
   * Subscribes to the result and updates the UI accordingly.
   */
  private importProject(password: string) {
    this.importStep.set(5);
    this.importInProgress.set(true);
    const file = this.fileUploadForm.get('files')?.value?.[0];

    if (file && this.selectedVersionId() !== null) {
      this.projectService
        .importProject(
          file,
          this.projectNameForm.get('projectName')?.value,
          this.selectedVersionId()!,
          password,
        )
        .subscribe({
          next: (result) => {
            this.importInProgress.set(false);
            this.importComplete.set(true);
            this.warnings.set(result.warnings || []);
            this.importStep.set(6);
            this.appStateService.loadProjects().then(() => {
              this.appStateService.navigateToProject(
                result.projectId,
                result.currentVersionId,
              );
              this.dialogRef.close();
            });
            result.warnings.forEach((warning: string) => {
              this.toastr.warning(warning, 'Warning', {
                timeOut: 0,
                extendedTimeOut: 0,
              });
            });
          },
          error: (error) => {
            this.importInProgress.set(false);
            console.error('Import failed:', error);
          },
        });
    }
  }

  /**
   * Copies any warnings generated during the import process to the clipboard.
   */
  public copyWarnings() {
    const warningText = this.warnings().join('\n');
    navigator.clipboard.writeText(warningText).then(() => {
      console.log('Warnings copied to clipboard'); // Log success
    });
  }

  /**
   * Closes the import dialog and returns the success status and warnings to the caller.
   */
  public closeDialog() {
    this.dialogRef.close({
      success: this.importComplete(),
      warnings: this.warnings(),
    });
  }
}
